<template>
    <div
        :class="{ white, [dropdownDirection]: true }"
        class="menu-drop-down"
        @mouseenter="open"
        @mouseleave="close"
    >
        <button
            ref="menuButton"
            class="menu-button"
            tabindex="0"
            @click.prevent.stop="toggle"
            @focus.prevent.stop="''"
        >
            {{ label }}
        </button>

        <ul
            v-if="isOpen"
            ref="menuElement"
            class="menu"
            @keydown.escape.prevent.stop="close"
            @keydown.down.prevent.stop="focusNext"
            @keydown.up.prevent.stop="focusPrevious"
        >
            <li
                class="tab-catcher"
                tabindex="0"
                @focus.prevent.stop="focusItem(items.length - 1)"
            ></li>

            <li
                v-for="(item, index) in items"
                :key="index"
                ref="itemElements"
                v-qa="item.label"
                class="item"
                tabindex="0"
                @click="select(index)"
                @mouseenter="focusItem(index)"
                @keypress.enter.prevent.stop="select(index)"
            >
                {{ item.label }}
            </li>

            <li
                class="tab-catcher"
                tabindex="0"
                @focus.prevent.stop="focusItem(0)"
            ></li>
        </ul>
    </div>
</template>

<script lang="ts" setup>
import { nextTick, type Ref, ref } from 'vue'
import { onClickOutside } from '@vueuse/core'

export type MenuDropDownItem = {
    label: string
    icon?: string
}

const props = withDefaults(
    defineProps<{
        label: string
        items: MenuDropDownItem[]
        white?: boolean
        dropdownDirection?: 'up' | 'down'
    }>(),
    {
        dropdownDirection: 'up',
    }
)

const emit = defineEmits<{
    close: []
    select: [itemIndex: number]
}>()

const itemElements = ref<HTMLElement[]>([])
const focused = ref(0)

const focusItem = (index: number) => {
    if (index < 0) {
        focused.value = props.items.length - 1

        itemElements.value[focused.value]?.focus()
    } else if (index >= props.items.length) {
        focused.value = 0

        itemElements.value[focused.value]?.focus()
    } else {
        focused.value = index

        itemElements.value[focused.value]?.focus()
    }
}

const focusNext = () => focusItem(focused.value + 1)
const focusPrevious = () => focusItem(focused.value - 1)

const isOpen = ref(false)
const toggle = () => {
    isOpen.value = !isOpen.value

    if (isOpen.value) {
        nextTick(() => focusItem(0))
    }
}
const open = () => (isOpen.value = true)
const close = () => (isOpen.value = false)

const menuButton: Ref<HTMLElement | null> = ref(null)
const select = (item: number) => {
    emit('select', item)

    close()
    menuButton.value?.focus()
}

const menuElement: Ref<HTMLElement | null> = ref(null)
onClickOutside(menuElement, close, {
    ignore: [menuButton],
})
</script>

<style lang="scss" scoped>
.menu-drop-down {
    position: relative;
    background-color: $blue;
    font-family: $effra-font-stack;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.8rem;
    line-height: 50px;
    font-weight: 400;

    &.white {
        background-color: transparent;

        .menu {
            background-color: $white;
            color: $black;

            .item {
                color: $black;

                @include hover() {
                    color: $slate;
                }

                &:focus {
                    color: $slate;
                }
            }
        }

        .menu-button {
            color: $black;
        }
    }

    &.down {
        .menu {
            bottom: unset;
            right: unset;
            top: 30px;
            width: 100%;
            box-shadow: 0 0 15px rgba($black, 0.2);
        }
    }

    @include min-width(1024) {
        line-height: 29px;
        height: 29px;
    }

    .menu-button {
        user-select: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 30px;
        line-height: 30px;
        color: $white;
        margin: 0 10px 0 0;

        &::after {
            font-family: $icon-font;
            content: $icon-angle-down;
            font-weight: 600;
            font-size: 11px;
            margin-left: 5px;
        }
    }

    .menu {
        position: absolute;
        right: -10px;
        bottom: 30px;
        min-width: 60px;
        z-index: 2;
        background: $blue;
        line-height: 1em;
        padding: 10px 0;

        @include min-width(1024) {
            left: -10px;
            top: 30px;
            right: initial;
            bottom: initial;
        }

        .item {
            user-select: none;
            cursor: pointer;
            list-style: none;
            padding: 10px;
            width: 100%;
            white-space: nowrap;
            color: rgba($white, 0.7);

            &:focus {
                color: $white;
            }

            @include hover() {
                color: $white;
            }
        }

        .tab-catcher {
            position: absolute;
            width: 1px;
            height: 1px;
            overflow: hidden;
        }
    }
}
</style>
